/**
 * F17 T17.11.7 + T17.11.B + T17.11.C — galeria wzorów procesów (tenant scope).
 *
 * Strona prezentuje bibliotekę opublikowanych szablonów procesów zarządzanych
 * przez administratora platformy. Tenant może:
 *   a) skopiować wzór i dostosować go w swoim procesie,
 *   b) użyć wzoru wprost jako powiązanego procesu (live-read z szablonu ADM),
 *   c) włączyć / wyłączyć wzór (wymaga uprawnienia processes.templates.toggle).
 *
 * Przepływ:
 *  1. GET /api/process-templates — lista kart wzorów.
 *  2a. Klik „Kopiuj i dostosuj" → ConfirmDialog → POST /copy → builder.
 *  2b. Klik „Użyj wprost" → ConfirmDialog → POST /use → lista procesów.
 *  2c. Klik „Wyłącz/Włącz" → ConfirmDialog → POST /disable|/enable → invalidate listy.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, FileText, Link, Power } from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  applyProcessTemplate,
  copyProcessTemplate,
  disableProcessTemplate,
  enableProcessTemplate,
  listProcessTemplates,
  type ProcessTemplateSummary,
} from '@/lib/processes/templates-api';

// ============================================================================
// Helpers
// ============================================================================

/** Mapa kodu modułu na ludzką etykietę — reużywa istniejących kluczy i18n. */
function useModuleLabel() {
  const { t } = useTranslation();
  return (code: string): string => {
    const key = `tenant.processes.moduleLabel.${code.replace(/\./g, '_')}`;
    const result = t(key);
    // Jeśli klucz nie istnieje, i18next zwraca sam klucz (brak fallbacku) —
    // w takim przypadku pokaż surowy kod modułu.
    return result === key ? code : result;
  };
}

// ============================================================================
// TemplateCard
// ============================================================================

interface TemplateCardProps {
  template: ProcessTemplateSummary;
  onCopy: (template: ProcessTemplateSummary) => void;
  onUse: (template: ProcessTemplateSummary) => void;
  onToggle: (template: ProcessTemplateSummary) => void;
  isPending: boolean;
  canToggle: boolean;
}

function TemplateCard({
  template,
  onCopy,
  onUse,
  onToggle,
  isPending,
  canToggle,
}: TemplateCardProps) {
  const { t } = useTranslation();
  const getModuleLabel = useModuleLabel();

  const isDisabled = !template.isEnabled;

  return (
    <div
      className={`flex flex-col rounded-xl border p-5 shadow-sm transition-shadow hover:shadow-md ${isDisabled ? 'border-border-primary bg-bg-primary opacity-60' : 'border-border-primary bg-bg-secondary'}`}
      data-testid={`template-card-${template.id}`}
      data-enabled={template.isEnabled ? 'true' : 'false'}
    >
      {/* Ikona + nazwa */}
      <div className="flex items-start gap-3">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${isDisabled ? 'bg-bg-secondary text-text-secondary' : 'bg-accent-50 text-accent-600 dark:bg-accent-900/20 dark:text-accent-400'}`}
        >
          <FileText className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <h3
            className={`truncate text-base font-semibold ${isDisabled ? 'text-text-secondary' : 'text-text-primary'}`}
            title={template.name}
            data-testid={`template-card-name-${template.id}`}
          >
            {template.name}
          </h3>
          <span className="mt-0.5 inline-block rounded-full bg-bg-primary px-2 py-0.5 text-xs font-medium text-text-secondary ring-1 ring-border-primary">
            {getModuleLabel(template.targetModuleCode)}
          </span>
        </div>
        {/* Przycisk Wyłącz/Włącz — widoczny tylko gdy ma uprawnienie */}
        {canToggle && (
          <button
            type="button"
            onClick={() => onToggle(template)}
            disabled={isPending}
            className={`shrink-0 flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${isDisabled ? 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/40' : 'bg-bg-primary text-text-secondary hover:bg-bg-secondary ring-1 ring-border-primary'}`}
            data-testid={`template-card-toggle-btn-${template.id}`}
            aria-label={
              isDisabled
                ? t('tenant.processes.templates.enableAriaLabel', { name: template.name })
                : t('tenant.processes.templates.disableAriaLabel', { name: template.name })
            }
          >
            <Power className="h-3 w-3" aria-hidden="true" />
            {isDisabled
              ? t('tenant.processes.templates.enableButton')
              : t('tenant.processes.templates.disableButton')}
          </button>
        )}
      </div>

      {/* Opis */}
      {template.description && (
        <p className="mt-3 line-clamp-3 text-sm text-text-secondary">{template.description}</p>
      )}

      {/* Footer: wersja + przyciski */}
      <div className="mt-4 flex items-center justify-between gap-2">
        <span className="shrink-0 text-xs text-text-secondary">
          {t('tenant.processes.templates.version', { version: template.version })}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onUse(template)}
            disabled={isPending || isDisabled}
            className="flex items-center gap-1.5 rounded-lg border border-accent-600 px-3 py-1.5 text-sm font-medium text-accent-700 hover:bg-accent-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-accent-400 dark:hover:bg-accent-900/20"
            data-testid={`template-card-use-btn-${template.id}`}
            aria-label={t('tenant.processes.templates.useAriaLabel', { name: template.name })}
          >
            <Link className="h-3.5 w-3.5" aria-hidden="true" />
            {t('tenant.processes.templates.useButton')}
          </button>
          <button
            type="button"
            onClick={() => onCopy(template)}
            disabled={isPending || isDisabled}
            className="flex items-center gap-1.5 rounded-lg bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid={`template-card-copy-btn-${template.id}`}
            aria-label={t('tenant.processes.templates.copyAriaLabel', { name: template.name })}
          >
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            {t('tenant.processes.templates.copyButton')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Page
// ============================================================================

export function ProcessTemplatesGalleryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { me } = useTenantAuth();

  const canToggle =
    me?.user.isSuperUser === true ||
    me?.permissions.includes('processes.templates.toggle') === true;

  const [pendingCopy, setPendingCopy] = useState<ProcessTemplateSummary | null>(null);
  const [pendingUse, setPendingUse] = useState<ProcessTemplateSummary | null>(null);
  const [pendingToggle, setPendingToggle] = useState<ProcessTemplateSummary | null>(null);

  // Pobieranie biblioteki wzorów
  const templatesQuery = useQuery({
    queryKey: ['process-templates'],
    queryFn: listProcessTemplates,
    staleTime: 60_000,
  });

  // Mutacja kopiowania wzoru
  const copyMutation = useMutation({
    mutationFn: (templateId: string) => copyProcessTemplate(templateId),
    onSuccess: (result) => {
      if (result.ok) {
        toast.show({
          variant: 'success',
          title: t('tenant.processes.templates.toast.copySuccess'),
        });
        // Unieważnij listę procesów (nowy process pojawił się na liście).
        void queryClient.invalidateQueries({ queryKey: ['processes'] });
        // Przejdź do buildera nowego procesu.
        navigate(`/processes/builder/${result.processId}`);
      } else if (result.error === 'PERMISSION_DENIED') {
        toast.show({
          variant: 'error',
          title: t('tenant.processes.templates.toast.permissionDenied'),
        });
      } else if (result.error === 'TEMPLATE_NOT_FOUND') {
        toast.show({
          variant: 'error',
          title: t('tenant.processes.templates.toast.templateNotFound'),
        });
        // Wzór mógł zostać wycofany — odśwież listę.
        void templatesQuery.refetch();
      } else if (result.error === 'TEMPLATE_DISABLED') {
        toast.show({
          variant: 'error',
          title: t('tenant.processes.templates.toast.disabledError'),
        });
        // Lista może być nieaktualna — szablon właśnie wyłączono.
        void queryClient.invalidateQueries({ queryKey: ['process-templates'] });
      } else {
        toast.show({
          variant: 'error',
          title: t('tenant.processes.templates.toast.copyError'),
        });
      }
      setPendingCopy(null);
    },
  });

  // Mutacja użycia wzoru wprost (linked process)
  const applyMutation = useMutation({
    mutationFn: (templateId: string) => applyProcessTemplate(templateId),
    onSuccess: (result) => {
      if (result.ok) {
        toast.show({
          variant: 'success',
          title: t('tenant.processes.templates.toast.useSuccess'),
        });
        // Unieważnij listę procesów (nowy powiązany proces pojawił się na liście).
        void queryClient.invalidateQueries({ queryKey: ['processes'] });
        // Przejdź do listy procesów (nie do buildera — jest read-only).
        navigate('/processes');
      } else if (result.error === 'PERMISSION_DENIED') {
        toast.show({
          variant: 'error',
          title: t('tenant.processes.templates.toast.usePermissionDenied'),
        });
      } else if (result.error === 'TEMPLATE_NOT_FOUND') {
        toast.show({
          variant: 'error',
          title: t('tenant.processes.templates.toast.useTemplateNotFound'),
        });
        void templatesQuery.refetch();
      } else if (result.error === 'TEMPLATE_DISABLED') {
        toast.show({
          variant: 'error',
          title: t('tenant.processes.templates.toast.disabledError'),
        });
        // Lista może być nieaktualna — szablon właśnie wyłączono.
        void queryClient.invalidateQueries({ queryKey: ['process-templates'] });
      } else {
        toast.show({
          variant: 'error',
          title: t('tenant.processes.templates.toast.useError'),
        });
      }
      setPendingUse(null);
    },
  });

  // Mutacja toggle (włącz / wyłącz wzór)
  const toggleMutation = useMutation({
    mutationFn: (template: ProcessTemplateSummary) =>
      template.isEnabled ? disableProcessTemplate(template.id) : enableProcessTemplate(template.id),
    onSuccess: (result, template) => {
      if (result.ok) {
        toast.show({
          variant: 'success',
          title: template.isEnabled
            ? t('tenant.processes.templates.toast.disableSuccess')
            : t('tenant.processes.templates.toast.enableSuccess'),
        });
        void queryClient.invalidateQueries({ queryKey: ['process-templates'] });
      } else if (result.error === 'PERMISSION_DENIED') {
        toast.show({
          variant: 'error',
          title: t('tenant.processes.templates.toast.togglePermissionDenied'),
        });
      } else {
        toast.show({
          variant: 'error',
          title: template.isEnabled
            ? t('tenant.processes.templates.toast.disableError')
            : t('tenant.processes.templates.toast.enableError'),
        });
      }
      setPendingToggle(null);
    },
    onError: (_err, template) => {
      toast.show({
        variant: 'error',
        title: template.isEnabled
          ? t('tenant.processes.templates.toast.disableError')
          : t('tenant.processes.templates.toast.enableError'),
      });
      setPendingToggle(null);
    },
  });

  const isPending = copyMutation.isPending || applyMutation.isPending || toggleMutation.isPending;

  function handleCopyClick(template: ProcessTemplateSummary) {
    setPendingCopy(template);
  }

  function handleUseClick(template: ProcessTemplateSummary) {
    setPendingUse(template);
  }

  function handleToggleClick(template: ProcessTemplateSummary) {
    setPendingToggle(template);
  }

  function handleConfirmCopy() {
    if (!pendingCopy || copyMutation.isPending) return;
    copyMutation.mutate(pendingCopy.id);
  }

  function handleConfirmUse() {
    if (!pendingUse || applyMutation.isPending) return;
    applyMutation.mutate(pendingUse.id);
  }

  function handleConfirmToggle() {
    if (!pendingToggle || toggleMutation.isPending) return;
    toggleMutation.mutate(pendingToggle);
  }

  const templates = templatesQuery.data?.data ?? [];
  const isEmpty = !templatesQuery.isLoading && templates.length === 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6" data-testid="process-templates-gallery">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={() => navigate('/processes')}
            className="mb-2 flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
            data-testid="templates-gallery-back-btn"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t('tenant.processes.templates.backToProcesses')}
          </button>
          <h1 className="text-2xl font-semibold text-text-primary">
            {t('tenant.processes.templates.title')}
          </h1>
          <p className="mt-1 text-sm text-text-secondary">
            {t('tenant.processes.templates.subtitle')}
          </p>
        </div>
      </div>

      {/* Stan ładowania */}
      {templatesQuery.isLoading && (
        <div
          className="rounded-lg border border-border-primary bg-bg-secondary p-8 text-center text-sm text-text-secondary"
          data-testid="templates-gallery-loading"
        >
          {t('tenant.processes.templates.loading')}
        </div>
      )}

      {/* Stan błędu */}
      {templatesQuery.isError && !templatesQuery.isLoading && (
        <div
          className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300"
          data-testid="templates-gallery-error"
        >
          {t('tenant.processes.templates.loadError')}
        </div>
      )}

      {/* Stan pusty */}
      {isEmpty && !templatesQuery.isError && (
        <div
          className="rounded-xl border border-border-primary bg-bg-secondary p-12 text-center"
          data-testid="templates-gallery-empty"
        >
          <FileText className="mx-auto mb-4 h-10 w-10 text-text-secondary" aria-hidden="true" />
          <p className="text-sm text-text-secondary">{t('tenant.processes.templates.empty')}</p>
        </div>
      )}

      {/* Siatka kart wzorów */}
      {!templatesQuery.isLoading && templates.length > 0 && (
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="templates-gallery-grid"
        >
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onCopy={handleCopyClick}
              onUse={handleUseClick}
              onToggle={handleToggleClick}
              isPending={isPending}
              canToggle={canToggle}
            />
          ))}
        </div>
      )}

      {/* Dialog potwierdzenia kopiowania */}
      <ConfirmDialog
        open={pendingCopy !== null}
        variant="primary"
        title={t('tenant.processes.templates.confirmCopy.title')}
        message={
          pendingCopy
            ? t('tenant.processes.templates.confirmCopy.message', {
                name: pendingCopy.name,
              })
            : ''
        }
        confirmLabel={t('tenant.processes.templates.confirmCopy.confirm')}
        onConfirm={handleConfirmCopy}
        onClose={() => setPendingCopy(null)}
      />

      {/* Dialog potwierdzenia użycia wprost */}
      <ConfirmDialog
        open={pendingUse !== null}
        variant="primary"
        title={t('tenant.processes.templates.confirmUse.title')}
        message={
          pendingUse
            ? t('tenant.processes.templates.confirmUse.message', {
                name: pendingUse.name,
              })
            : ''
        }
        confirmLabel={t('tenant.processes.templates.confirmUse.confirm')}
        onConfirm={handleConfirmUse}
        onClose={() => setPendingUse(null)}
      />

      {/* Dialog potwierdzenia toggle (włącz/wyłącz wzór) */}
      <ConfirmDialog
        open={pendingToggle !== null}
        variant="primary"
        title={
          pendingToggle?.isEnabled
            ? t('tenant.processes.templates.confirmDisable.title')
            : t('tenant.processes.templates.confirmEnable.title')
        }
        message={
          pendingToggle
            ? pendingToggle.isEnabled
              ? t('tenant.processes.templates.confirmDisable.message', {
                  name: pendingToggle.name,
                })
              : t('tenant.processes.templates.confirmEnable.message', {
                  name: pendingToggle.name,
                })
            : ''
        }
        confirmLabel={
          pendingToggle?.isEnabled
            ? t('tenant.processes.templates.confirmDisable.confirm')
            : t('tenant.processes.templates.confirmEnable.confirm')
        }
        onConfirm={handleConfirmToggle}
        onClose={() => setPendingToggle(null)}
      />
    </div>
  );
}
